// BUILD-046 pass F1 (Owner direction 2026-10-25): the small "i" button that holds a useful explanation so it does not sit on the form as a paragraph. A disclosure: the button has an accessible name
// ("About <label>"), reports aria-expanded, opens a short note beside it (drawn on the page body, so no clipping container cuts it off) and closes on Escape, on a click elsewhere or on a second press.
// It is for EXPLANATIONS only. An error, a required field, a date-change reason or a restriction the person must act on is written next to its control, never put in here.
import { useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { FloatingPanel } from './FloatingPanel';

export function InfoButton({ label, children, testId }: { label: string; children: ReactNode; testId?: string }) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const panelId = useId();
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      setOpen(false);
      buttonRef.current?.focus();
    };
    const onDown = (event: MouseEvent): void => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (buttonRef.current?.contains(target) === true) return;
      if (document.getElementById(panelId)?.contains(target) === true) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, panelId]);
  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label={`About ${label}`}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        data-testid={testId}
        onClick={() => setOpen((value) => !value)}
        className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border border-gray-400 text-[11px] font-semibold leading-none text-gray-600 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8]"
      >
        <span aria-hidden="true">i</span>
      </button>
      {open && (
        <FloatingPanel anchorRef={buttonRef} id={panelId} role="note" ariaLabel={label} minWidth={240} wantHeight={200} className="rounded border border-gray-200 bg-white p-2 text-xs text-gray-800 shadow-lg" testId="info-note">
          {children}
        </FloatingPanel>
      )}
    </>
  );
}
